import React, { useState } from "react";

interface InvitationPreviewModalProps {
  recipientEmail: string;
  recipientName?: string;
  defaultMessage: string;
  inviterName: string;
  inviterEmail: string;
  systemEmail: string;
  inviteType: "project" | "uploader" | "redirect";
  projectTitle?: string;   // project name (project/uploader) or request title (redirect)
  onSend: (customMessage: string) => void;
  onCancel: () => void;
}

/** Build the pre-filled body text that mirrors the backend default template. */
function buildDefaultBody(
  inviteType: "project" | "uploader" | "redirect",
  recipientEmail: string,
  recipientName: string | undefined,
  inviterName: string,
  projectTitle: string | undefined  // project name for project/uploader; request title for redirect
): string {
  const greeting = recipientName ? `Hello ${recipientName},` : `Hello ${recipientEmail},`;

  if (inviteType === "project") {
    return (
      `${greeting}\n\n` +
      `You have been invited to join a research project.\n\n` +
      `Project: ${projectTitle || ""}\n` +
      `Invited By: ${inviterName}\n\n` +
      `Click the link below to accept the invitation and gain access to the project.`
    );
  }

  if (inviteType === "redirect") {
    return (
      `${inviterName} has forwarded a data request to you and is requesting your help.\n\n` +
      `Request Title: ${projectTitle || ""}\n\n` +
      `Please review the request and respond at your earliest convenience.`
    );
  }

  return (
    `${greeting}\n\n` +
    `You have been invited to upload data for a research project.\n\n` +
    `${inviterName} is requesting data for the ${projectTitle || ""} project.\n\n` +
    `Please use the link below to securely upload your data. `
  );
}

export const InvitationPreviewModal: React.FC<InvitationPreviewModalProps> = ({
  recipientEmail,
  recipientName,
  defaultMessage,
  inviterName,
  inviterEmail,
  systemEmail,
  inviteType,
  projectTitle,
  onSend,
  onCancel,
}) => {
  const prefilled = defaultMessage || buildDefaultBody(
    inviteType, recipientEmail, recipientName, inviterName, projectTitle
  );
  const [message, setMessage] = useState(prefilled);

  const headerColor =
    inviteType === "project" ? "#6f42c1" :
    inviteType === "redirect" ? "#f59e0b" : "#fd7e14";
  const subject =
    inviteType === "project"
      ? `You've Been Invited to Join Project: ${projectTitle || ""}`
      : inviteType === "redirect"
      ? `Data Request Forwarded to You`
      : `Data Upload Request${projectTitle ? `: ${projectTitle}` : ""}`;
  const actionLabel =
    inviteType === "project" ? "Accept Invitation" :
    inviteType === "redirect" ? "View Request" : "Upload Data";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          width: "680px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "14px 20px",
            background: "white",
            borderBottom: "1px solid #dee2e6",
            borderRadius: "8px 8px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "15px", color: "#495057" }}>
            Preview &amp; Edit Invitation Email
          </h3>
          <button
            onClick={onCancel}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#6c757d",
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>

        {/* Envelope info bar */}
        <div
          style={{
            padding: "12px 20px",
            background: "white",
            borderBottom: "1px solid #e9ecef",
            fontSize: "13px",
            lineHeight: "1.8",
          }}
        >
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>From:</span>
            <span style={{ color: "#6c757d" }}>{systemEmail}</span>
          </div>
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>To:</span>
            <span>{recipientEmail}</span>
          </div>
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>CC:</span>
            <span style={{ color: "#6c757d" }}>{inviterEmail}</span>
            <span style={{ fontSize: "11px", color: "#adb5bd", marginLeft: "6px" }}>(you will receive a copy)</span>
          </div>
          <div>
            <span style={{ fontWeight: 600, color: "#495057", display: "inline-block", width: "60px" }}>Subject:</span>
            <span>{subject}</span>
          </div>
        </div>

        {/* Email preview */}
        <div style={{ padding: "20px" }}>
          <div
            style={{
              maxWidth: "560px",
              margin: "0 auto",
              fontFamily: "Arial, sans-serif",
              color: "#333",
              background: "white",
              borderRadius: "5px",
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            {/* Email header banner */}
            <div
              style={{
                background: headerColor,
                color: "white",
                padding: "20px",
                textAlign: "center",
                borderRadius: "5px 5px 0 0",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}>{subject}</h2>
            </div>

            {/* Email body */}
            <div
              style={{
                padding: "24px",
                background: "#f9f9f9",
                border: "1px solid #ddd",
                borderTop: "none",
                borderRadius: "0 0 5px 5px",
              }}
            >
              {/* Summary bar (fixed) */}
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: "16px",
                  padding: "12px",
                  background: "#e8f4f8",
                  borderLeft: `4px solid ${headerColor}`,
                  borderRadius: "0 4px 4px 0",
                }}
              >
                {inviteType === "project"
                  ? `${inviterName} invited you to collaborate on '${projectTitle || ""}'`
                  : inviteType === "redirect"
                  ? `${inviterName} forwarded a data request: '${projectTitle || ""}'`
                  : `${inviterName} is requesting data for the ${projectTitle ? `'${projectTitle}'` : "research"} project`}
              </div>

              {/* Editable body — full email text */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "11px",
                    color: "#adb5bd",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "6px",
                  }}
                >
                  Email body — edit freely
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={10}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `1px solid ${headerColor}55`,
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "Arial, sans-serif",
                    lineHeight: "1.6",
                    resize: "vertical",
                    boxSizing: "border-box",
                    background: "#fffff8",
                    color: "#333",
                    outline: "none",
                  }}
                />
              </div>

              {/* Action button preview (fixed) */}
              <div style={{ textAlign: "center", margin: "20px 0" }}>
                <span
                  style={{
                    display: "inline-block",
                    padding: "12px 30px",
                    background: headerColor,
                    color: "white",
                    borderRadius: "5px",
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  {actionLabel}
                </span>
              </div>

              {/* Footer (fixed) */}
              <div
                style={{
                  textAlign: "center",
                  paddingTop: "16px",
                  borderTop: "1px solid #eee",
                  color: "#999",
                  fontSize: "11px",
                }}
              >
                Pseudonymization Service — Secure Data Anonymization
                <br />
                This is an automated notification. Please do not reply to this email.
              </div>
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div
          style={{
            padding: "12px 20px",
            background: "white",
            borderTop: "1px solid #dee2e6",
            borderRadius: "0 0 8px 8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => setMessage(prefilled)}
            style={{
              padding: "6px 14px",
              background: "white",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#6c757d",
            }}
          >
            Reset to default
          </button>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              style={{
                padding: "8px 20px",
                background: "white",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                color: "#495057",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => onSend(message)}
              style={{
                padding: "8px 20px",
                background: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Send Invitation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
